import React, { useState } from "react";
import { Phone, Mail, MapPin, Factory, Send, MessageCircle } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal, SectionHeading } from "../components/Common";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { IMAGES, BRAND, API, waLink } from "../mock/mock";

const DIRECTORY = [
  { name: "Meilleur Oudh & Aromas", detail: "sales.meilleurplantations@gmail.com · +91 99104 44617 / +91 97177 21117", sub: "Plantation & Distillation — Golaghat, Assam" },
  { name: "Meilleur Plantations — Group", detail: "ceo.meilleurplantations@gmail.com · +91 99104 44617", sub: "A-93, Ashoka Enclave II, Sector 37, Faridabad, Haryana 121003" },
  { name: "The Scent Lab — Retail", detail: "Call / WhatsApp +91 98109 06220", sub: "B-72 (4th floor), Ashoka Enclave II, Faridabad · Delhi NCR & Indore" },
];

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email) {
      toast({ title: "Please complete required fields", description: "First name and email are required." });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: "contact" }),
      });
      if (!res.ok) throw new Error("Request failed");
      setForm({ firstName: "", lastName: "", phone: "", email: "", message: "" });
      toast({ title: "Enquiry received", description: "Thank you — our team will be in touch shortly." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again, or reach us on WhatsApp." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-noir">
      <Seo
        title="Contact — Oud Trade & Export Enquiries | Meilleur Oudh & Aromas"
        description="Get in touch with Meilleur Oudh & Aromas for oud chips, oud oil and bakhoor trade, export and partnership enquiries. Serving the GCC, Europe and worldwide."
        path="/contact"
        keywords="oud supplier contact, agarwood export enquiry, buy oud wholesale, oud trade partner, Meilleur contact"
      />
      <PageHero eyebrow="Trade & Partnerships" title="Get in" accent="Touch" image={IMAGES.blueSmoke} crumbs={["Contact"]} />

      <section className="py-28">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-16">
          {/* Info */}
          <div className="lg:col-span-5">
            <Reveal><SectionHeading eyebrow="Reach Us" title="Let's build the next" accent="chapter" /></Reveal>
            <Reveal delay={120}>
              <p className="mt-7 text-[var(--ivory-muted)] leading-relaxed font-body font-light max-w-md">
                Whether you're a trade partner in the Gulf, a retailer, or a farmer exploring our inoculum programme —
                we'd love to hear from you.
              </p>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-10 space-y-6">
                {[
                  { icon: Phone, l: `${BRAND.phone} / ${BRAND.phoneAlt}` },
                  { icon: Mail, l: BRAND.email },
                  { icon: MapPin, l: BRAND.address },
                  { icon: Factory, l: BRAND.factory },
                ].map((c, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-11 h-11 shrink-0 border border-[var(--line)] flex items-center justify-center">
                      <c.icon size={17} className="text-[var(--gold)]" />
                    </div>
                    <p className="text-[var(--ivory-muted)] font-body pt-2.5">{c.l}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal delay={140}>
              <form onSubmit={submit} className="lux-card p-8 md:p-12">
                <h3 className="font-serif-display text-3xl mb-8">Send an <span className="gold-text italic">enquiry</span></h3>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="eyebrow text-[0.6rem]">First Name *</Label>
                    <Input name="firstName" value={form.firstName} onChange={handle} className="mt-3 bg-[var(--panel-2)] border-[var(--line)] h-12 focus:border-[var(--gold)] text-[var(--ivory)]" />
                  </div>
                  <div>
                    <Label className="eyebrow text-[0.6rem]">Last Name</Label>
                    <Input name="lastName" value={form.lastName} onChange={handle} className="mt-3 bg-[var(--panel-2)] border-[var(--line)] h-12 focus:border-[var(--gold)] text-[var(--ivory)]" />
                  </div>
                  <div>
                    <Label className="eyebrow text-[0.6rem]">Phone</Label>
                    <Input name="phone" value={form.phone} onChange={handle} className="mt-3 bg-[var(--panel-2)] border-[var(--line)] h-12 focus:border-[var(--gold)] text-[var(--ivory)]" />
                  </div>
                  <div>
                    <Label className="eyebrow text-[0.6rem]">Email *</Label>
                    <Input name="email" type="email" value={form.email} onChange={handle} className="mt-3 bg-[var(--panel-2)] border-[var(--line)] h-12 focus:border-[var(--gold)] text-[var(--ivory)]" />
                  </div>
                </div>
                <div className="mt-6">
                  <Label className="eyebrow text-[0.6rem]">Message</Label>
                  <Textarea name="message" value={form.message} onChange={handle} rows={5} className="mt-3 bg-[var(--panel-2)] border-[var(--line)] focus:border-[var(--gold)] text-[var(--ivory)] resize-none" />
                </div>
                <button type="submit" disabled={loading} className="btn-gold mt-8 w-full sm:w-auto justify-center disabled:opacity-60">
                  {loading ? "Sending..." : "Send Now"} <Send size={15} />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Directory */}
      <section className="py-24 bg-noir-2 border-y border-[var(--line)]">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal><SectionHeading eyebrow="Group Directory" title="One house," accent="many doors" align="center" /></Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-7">
            {DIRECTORY.map((d, i) => (
              <Reveal key={d.name} delay={i * 100}>
                <div className="lux-card p-8 h-full">
                  <h3 className="font-serif-display text-xl mb-4 text-[var(--gold-light)]">{d.name}</h3>
                  <p className="text-sm text-[var(--ivory)] font-body leading-relaxed">{d.detail}</p>
                  <p className="text-xs text-[var(--ivory-muted)] font-body mt-3">{d.sub}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
